import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { PackageOpen } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL", "36", "37", "38", "39", "40"];

const Collection = ({ mode }) => {
  const params = useParams();
  const slug = params.slug;
  const { settings } = useStore();
  const [cat, setCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [price, setPrice] = useState([0, 7000]);
  const [sizes, setSizes] = useState([]);
  const [inStock, setInStock] = useState(false);

  const title = mode === "new" ? "New Arrivals" : mode === "offers" ? "Offers & Trending" : cat?.name || "Collection";
  const desc = mode === "new" ? "The freshest additions to the ANUSWA wardrobe." : mode === "offers" ? "Best deals and trending styles this season." : cat?.description || "";

  useEffect(() => {
    if (mode) return;
    api.get("/categories").then((r) => setCat(r.data.find((c) => c.slug === slug) || { name: slug, description: "" }));
  }, [slug, mode]);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (mode === "new") p.set("new_arrival", "true");
    else if (mode === "offers") p.set("trending", "true");
    else if (slug) p.set("category", slug);
    p.set("sort", sort);
    p.set("min_price", price[0]); p.set("max_price", price[1]);
    if (inStock) p.set("in_stock", "true");
    api.get(`/products?${p.toString()}`).then((r) => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug, mode, sort, price, inStock]);

  const filtered = useMemo(() => {
    if (!sizes.length) return products;
    return products.filter((p) => (p.sizes || []).some((s) => sizes.includes(s)));
  }, [products, sizes]);

  const toggleSize = (s) => setSizes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
      <nav className="mb-4 text-xs text-muted-text" data-testid="breadcrumb">
        <Link to="/" className="hover:text-burgundy">Home</Link> <span className="mx-1">/</span> <span className="text-charcoal">{title}</span>
      </nav>
      <h1 className="font-serif text-4xl text-burgundy">{title}</h1>
      {desc && <p className="mt-2 max-w-2xl text-sm text-muted-text">{desc}</p>}
      <p className="mt-2 text-xs uppercase tracking-widest text-muted-text" data-testid="product-count">{loading ? "Loading..." : `${filtered.length} Products`}</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
        {/* Filters */}
        <aside className="space-y-8" data-testid="filters">
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide">Price</h3>
            <Slider min={0} max={7000} step={100} value={price} onValueChange={setPrice} data-testid="price-slider" />
            <div className="mt-2 flex justify-between text-xs text-muted-text"><span>₹{price[0]}</span><span>₹{price[1]}</span></div>
          </div>
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide">Size</h3>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <button key={s} onClick={() => toggleSize(s)} data-testid={`filter-size-${s}`}
                  className={`min-w-[38px] border px-2 py-1 text-xs ${sizes.includes(s) ? "border-burgundy bg-burgundy text-white" : "border-border text-charcoal hover:border-burgundy"}`}>{s}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="filter-instock">
            <Checkbox checked={inStock} onCheckedChange={setInStock} /> In Stock Only
          </label>
        </aside>

        {/* Grid */}
        <div>
          <div className="mb-6 flex items-center justify-end">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-52 rounded-none" data-testid="sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recommended">Recommended</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="popular">Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse bg-muted" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-24 text-center" data-testid="no-products">
              <PackageOpen className="h-12 w-12 text-muted-text/40" />
              <p className="font-serif text-2xl text-charcoal">No styles found</p>
              <p className="text-sm text-muted-text">Try adjusting your filters or browse another category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Collection;
